/**
 * GSAP Page Transition Animations
 */

import { gsap } from 'gsap';

// Create transition overlay to prevent purple flash
function createOverlay() {
  let overlay = document.querySelector('.page-transition-overlay');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.className = 'page-transition-overlay';
    overlay.style.cssText = `
      position: fixed;
      inset: 0;
      background: #050507;
      z-index: 9999;
      pointer-events: none;
      opacity: 0;
    `;
    document.body.appendChild(overlay);
  }
  return overlay;
}

/**
 * Initialize smooth page transitions
 */
export function initPageTransitions() {
  const content = document.querySelector('.wp-site-blocks') || document.querySelector('.site-main');
  if (!content) return;

  // Create overlay for transitions
  const overlay = createOverlay();

  // Mark document as JS-ready immediately to cancel CSS fallback
  document.documentElement.classList.add('js-ready');

  // Fade out overlay on page load (in case it was visible from previous navigation)
  gsap.to(overlay, { opacity: 0, duration: 0.3, delay: 0.1 });

  // Use fromTo for explicit control - prevents any jitter from state mismatches
  // CSS sets opacity: 0, we handle the rest
  gsap.fromTo(
    content,
    { opacity: 0, y: 20 },
    {
      opacity: 1,
      y: 0,
      duration: 0.6,
      ease: 'power2.out',
      clearProps: 'transform' // Clean up inline transform after animation
    }
  );

  // Handle internal link clicks for smooth transitions
  document.querySelectorAll('a[href^="/"], a[href^="' + window.location.origin + '"]').forEach((link) => {
    // Skip links with specific classes, targets, or attributes
    if (
      link.classList.contains('no-transition') ||
      link.target === '_blank' ||
      link.hasAttribute('download') ||
      link.closest('.wp-block-navigation') // Skip navigation links for now
    ) {
      return;
    }

    link.addEventListener('click', (e) => {
      const href = link.getAttribute('href');

      // Skip hash links
      if (!href || href.startsWith('#')) return;

      e.preventDefault();

      // Exit animation
      transitionOut(() => {
        window.location.href = href;
      });
    });
  });
}

/**
 * Transition out animation
 */
export function transitionOut(callback) {
  const content = document.querySelector('.wp-site-blocks') || document.querySelector('.site-main');
  const overlay = document.querySelector('.page-transition-overlay') || createOverlay();

  if (!content) {
    callback?.();
    return;
  }

  // Fade in overlay to cover any purple flash during navigation
  gsap.to(overlay, {
    opacity: 1,
    duration: 0.3,
    ease: 'power2.in',
  });

  gsap.to(content, {
    opacity: 0,
    y: -20,
    duration: 0.4,
    ease: 'power2.in',
    onComplete: callback,
  });
}

/**
 * Transition in animation (for use with view transitions API or custom routing)
 */
export function transitionIn() {
  const content = document.querySelector('.wp-site-blocks') || document.querySelector('.site-main');
  if (!content) return;

  gsap.fromTo(
    content,
    {
      opacity: 0,
      y: 20,
    },
    {
      opacity: 1,
      y: 0,
      duration: 0.6,
      ease: 'power2.out',
    }
  );
}
